import React, {
  use,
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import {
  Grid,
  Typography,
  Box,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepButton,
} from "@mui/material";
import { getIn, useFormikContext } from "formik";
import { StyledTextField } from "@components/CustomInput/customInput";
import * as Yup from "yup";
import {
  ifExistingSlipRoad,
  ifHillySectionRetailNew,
  ifRuralSectionPrivateCommonNew,
  ifRuralSectionPrivateNoServiceNew,
  ifRuralSectionRetailNew,
  ifUrbanSectionPrivateCommonNew,
  ifUrbanSectionPrivateNoServiceNew,
  ifUrbanSectionRetailNew,
  // Residential Properties
  ifResidentialUrbanSectionPrivateCommonNew,
  ifResidentialUrbanSectionPrivateNoServiceNew,
  ifResidentialRuralSectionPrivateCommonNew,
  ifResidentialRuralSectionPrivateNoServiceNew,
  // Other Properties
  ifOtherUrbanSectionPrivateCommonNew,
  ifOtherUrbanSectionPrivateNoServiceNew,
  ifOtherRuralSectionPrivateCommonNew,
  ifOtherRuralSectionPrivateNoServiceNew,
  notPreviouslyApproved,
  SelectAmenityCategoryPrivate,
  SelectAmenityCategoryRetail,
  SelectServiceRoadTypePrivate,
  notPreviouslyApprovedSeekClarification,
} from "./data";
import { useSelector } from "react-redux";
import { RootReducerType } from "@store/store";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "@store/store";
import { get, set } from "lodash";
import {
  EnergizedFuelDialog,
  FuelStationDialog,
  RoadNotCompliedDialog,
  DecelerationlaneDialog,
  NearestIntersectionDialog,
  IntersectionRoadMedianDialog,
  DividedCarriagewayDialog,
  ProximityServiceRoadDialog,
} from "./modal/PenaltyModal";
import { InfoOutlined } from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { getPenalityFeeThunk } from "@modules/application-section/application-details/redux/services/thunk/DetailsThunk";
import {
  resetSiteRoadWaysState,
  setSiteRoadwaysField,
} from "../redux/siteRoadwaysSlice";

// Security utility function to validate and sanitize input - Memoized outside component
const dangerousPatterns =
  /<script|javascript:|onerror=|onload=|<iframe|eval\(|<object|<embed/i;
const fieldPatterns: Record<string, RegExp> = {
  loi_dealer_name: /^[a-zA-Z\s.'-]*$/,
  loi_name: /^[a-zA-Z\s.'-]*$/,
  loi_number: /^[a-zA-Z0-9/\-]*$/,
};

const sanitizeInput = (value: string, fieldName: string): string | null => {
  if (dangerousPatterns.test(value)) {
    return null;
  }
  const pattern = fieldPatterns[fieldName];
  if (pattern && !pattern.test(value)) {
    return null;
  }
  return value;
};

export type Field = {
  id?: string | number;
  name: string | number;
  title: string;
  type: "text" | "radio" | "select" | "date";
  options?: Array<string | { id: string | number; name: string }>;
  dataType?: "number" | "text" | "string";
  nestedFields?: Field[];
  showIf?: { field: string | number; value: any };
  alwaysShowNested?: boolean;
  validation?: {
    min?: number;
    max?: number;
    message?: string;
  };
};

export type Section = {
  title?: string;
  fields: any[];
};

type categoryOption = {
  name: string;
  id: string;
};

interface UtilityFormRendererProps {
  sections: Section[];
  handleFieldChange: (name: string | number, value: any) => void;
  restrictedFields?: string[];
}

interface GetPlaceholderParams {
  fieldName: string;
  locationTypeId: number;
}

interface MaxValues {
  length: number;
  width: number;
}

export const UtilityFormRendererRetail: React.FC<UtilityFormRendererProps> = ({
  sections,
  handleFieldChange,
  restrictedFields = [],
}) => {

  // console.log("access restrictedFields" , restrictedFields)

  const { values, setFieldValue, touched, errors, handleBlur } =
    useFormikContext<any>();
  const dispatch = useDispatch<AppDispatch>();
  // const [locationType, setLocationType] = React.useState<string>("");
  const { category_id, access_id, property_type_id } = useSelector((state: RootReducerType) => state.amenities,);
  const [showFuelStationDialog, setShowFuelStationDialog] = useState(false);
  const [showEnergizedFuelDialog, setShowEnergizedFuelDialog] = useState(false);
  const [showRoadNotCompliedDialog, setShowRoadNotCompliedDialog] =
    useState(false);
  const [showDecelerationlaneDialog, setShowDecelerationlaneDialog] =
    useState(false);
  const [showNearestIntersectionDialog, setShowNearestIntersectionDialog] =
    useState(false);
  const [intersectionRoadMedianDialog, setIntersectionRoadMedianDialog] =
    useState(false);
  const [showDividedCarriagewayDialog, setShowDividedCarriagewayDialog] =
    useState(false);
  const [showProximityServiceRoadDialog, setShowProximityServiceRoadDialog] =
    useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [penaltyFeeData, setPenaltyFeeData] = useState<any>(null);
  const [openLoiDateModal, setOpenLoiDateModal] = useState<boolean>(false);

  // Fetch penalty fee data on mount
  useEffect(() => {
    const fetchPenaltyFee = async () => {
      try {
        const response = await dispatch(getPenalityFeeThunk());
        if (response.payload) {
          setPenaltyFeeData(response?.payload?.data?.penalty_amount);
        }
      } catch (error) {
        console.error("Failed to fetch penalty fee data:", error);
      }
    };
    fetchPenaltyFee();
  }, [dispatch]);
  const isFirstRender = useRef(true);

  const getLocationSpecificSections = (): Section[] => {
    if (category_id === 2) {
      // Private: Handle based on property_type_id for new structure (8 cases)
      // Handle type coercion - service_road_type_id and property_type_id might be string or number
      const serviceRoadTypeId = typeof values.service_road_type_id === "string"
        ? Number(values.service_road_type_id)
        : values.service_road_type_id;

      const propertyTypeId = property_type_id;

      const locationType = values.location_type_id;

      // Check if property_type_id is Residential (7, 8, 9) or Other (10, 11, 12, 13, 14)
      const isResidentialProperty = [7, 8, 9].includes(propertyTypeId as any);
      const isOtherProperty = [10, 11, 12, 13, 14].includes(propertyTypeId as any);

      // If neither Residential nor Other property, fall back to old logic (or return empty)
      if (!isResidentialProperty && !isOtherProperty) {
        return [];
      }

      // Determine which section to return based on property type, location type, and service road type
      if (isResidentialProperty) {
        // Residential Properties
        switch (locationType) {
          case 1: // Urban
            if (serviceRoadTypeId === 2) {
              return ifResidentialUrbanSectionPrivateCommonNew;
            } else if (serviceRoadTypeId === 3) {
              return ifResidentialUrbanSectionPrivateNoServiceNew;
            } else if (serviceRoadTypeId === 1) {
              return []; // Fields are in base section with showIf conditions
            }
            break;
          case 2: // Rural
            if (serviceRoadTypeId === 2) {
              return ifResidentialRuralSectionPrivateCommonNew;
            } else if (serviceRoadTypeId === 3) {
              return ifResidentialRuralSectionPrivateNoServiceNew;
            } else if (serviceRoadTypeId === 1) {
              return []; // Fields are in base section with showIf conditions
            }
            break;
        }
      } else if (isOtherProperty) {
        // Other Properties
        switch (locationType) {
          case 1: // Urban
            if (serviceRoadTypeId === 2) {
              return ifOtherUrbanSectionPrivateCommonNew;
            } else if (serviceRoadTypeId === 3) {
              return ifOtherUrbanSectionPrivateNoServiceNew;
            } else if (serviceRoadTypeId === 1) {
              return []; // Fields are in base section with showIf conditions
            }
            break;
          case 2: // Rural
            if (serviceRoadTypeId === 2) {
              return ifOtherRuralSectionPrivateCommonNew;
            } else if (serviceRoadTypeId === 3) {
              return ifOtherRuralSectionPrivateNoServiceNew;
            } else if (serviceRoadTypeId === 1) {
              return []; // Fields are in base section with showIf conditions
            }
            break;
        }
      }

      return [];
    } else {
      // Retail: All options allowed
      switch (values.location_type_id) {
        case 1:
          return ifUrbanSectionRetailNew;
        case 2:
          return ifRuralSectionRetailNew;
        case 3:
          return ifHillySectionRetailNew;
        // case 4:
        //     return ifMountainousSectionRetailNew;
        default:
          return [];
      }
    }
  };

  const allFieldsFilled =
    values?.plot_area !== null &&
    values?.plot_length_m !== "" &&
    values?.plot_width_m !== "" &&
    values?.plot_area !== undefined &&
    values?.plot_length_m !== undefined &&
    values?.plot_width_m !== undefined;

  const isFirstLocationSet = React.useRef(true);
  const isFirstServiceRoadSet = React.useRef(true);

  const isServiceRoadProvided = get(values, "is_service_road_provided", "");
  const isServiceListVisible = get(values, "service_road_type_id", "");

  // Handle type coercion for service_road_type_id (might be string or number)
  const normalizedServiceListVisible =
    typeof isServiceListVisible === "string"
      ? Number(isServiceListVisible)
      : isServiceListVisible;

  const isFieldRestricted = useCallback(
    (field: Field): boolean => {
      if (!restrictedFields || restrictedFields.length === 0) return false;

      const fieldName = String(field.name);
      const fieldId = field.id !== undefined ? String(field.id) : "";

      return restrictedFields.some(
        (rf) => rf === fieldName || (fieldId !== "" && rf === fieldId)
      );
    },
    [restrictedFields]
  );

  // Check if conditions are met to show location-specific sections
  // Must match parent component's logic exactly
  // For Retail: Check is_service_road_provided
  // For Private: Check service_road_type_id
  const shouldShowAccessCriteriaMet =
    category_id === 2
      ? allFieldsFilled &&
      (normalizedServiceListVisible === 1 ||
        normalizedServiceListVisible === 2 ||
        normalizedServiceListVisible === 3)
      : (allFieldsFilled && isServiceRoadProvided === "No") ||
      (allFieldsFilled &&
        (normalizedServiceListVisible === 1 ||
          normalizedServiceListVisible === 2 ||
          normalizedServiceListVisible === 3));

  useEffect(() => {
    if (shouldShowAccessCriteriaMet) {
      // if (category_id === 1) {
      //     handleFieldChange('access_criteria_met', 'Yes');
      // }
      // if (category_id === 2) {
      //   handleFieldChange("service_road_type_id", 3);
      // } else {
      //   handleFieldChange("service_road_type_id", 3);
      // }
    }
    getLocationSpecificSections();
  }, [shouldShowAccessCriteriaMet]);

  useEffect(() => {
    if (values.is_service_road_provided === "No") {
      // Clear fields that are hidden when "No" is selected
      if (values.on_slip_road !== "") {
        handleFieldChange("on_slip_road", "");
      }
      if (values.access_criteria_met !== "") {
        handleFieldChange("access_criteria_met", "");
      }
    }
    // if (values.is_service_road_provided === "Yes") {
    //   // Clear fields that are hidden when "Yes" is selected
    //   if (values.is_constructed_without_prior_permission !== "") {
    //     handleFieldChange("is_constructed_without_prior_permission", "");
    //   }
    //   if (values.is_facility_operational !== "") {
    //     handleFieldChange("is_facility_operational", "");
    //   }
    // }
  }, [values.is_service_road_provided]);

  // Clear LOI fields when COCO fuel station is set to "Yes"
  useEffect(() => {
    if (values.is_coco_fuel_station === "Yes") {
      // Clear all LOI fields when COCO is Yes
      if (values.loi_name !== "") {
        setFieldValue("loi_name", "");
        handleFieldChange("loi_name", "");
      }
      if (values.loi_dealer_name !== "") {
        setFieldValue("loi_dealer_name", "");
        handleFieldChange("loi_dealer_name", "");
      }
      if (values.loi_number !== "") {
        setFieldValue("loi_number", "");
        handleFieldChange("loi_number", "");
      }
      if (values.loi_date !== "") {
        setFieldValue("loi_date", "");
        handleFieldChange("loi_date", "");
      }
    }
  }, [values.is_coco_fuel_station]);

  useEffect(() => {
    const selectedCarriagewayType = values.fuel_station_carriageway_type;
    const undividedFields = ["77", "78", "299", "carriageway_fs_distance", "undivided_carriageway_distance_from_fs"];
    const dividedFields = ["399", "432", "433", "divided_carriageway_distance_from_fs"];

    if (selectedCarriagewayType === "Yes") {
      dividedFields.forEach((fieldName) => {
        if (values[fieldName] !== "" && values[fieldName] !== undefined) {
          setFieldValue(fieldName, "");
          handleFieldChange(fieldName, "");
        }
      });
    } else if (selectedCarriagewayType === "No") {
      undividedFields.forEach((fieldName) => {
        if (values[fieldName] !== "" && values[fieldName] !== undefined) {
          setFieldValue(fieldName, "");
          handleFieldChange(fieldName, "");
        }
      });
    }
  }, [values.fuel_station_carriageway_type]);

  const dynamicSections = useMemo(() => {
    const result = [
      ...sections.slice(0, 1),
      ...(shouldShowAccessCriteriaMet ? getLocationSpecificSections() : []),
    ];

    return result;
  }, [
    shouldShowAccessCriteriaMet,
    values.location_type_id,
    values.service_road_type_id,
  ]);

  // Enhanced field visibility check - matches parent validation logic
  const shouldShow = (field: Field) => {
    if (!field.showIf) return true;
    const conditionField = field.showIf.field.toString();
    const conditionValue = field.showIf.value;
    const currentValue = getIn(values, conditionField);

    // Handle type coercion for numeric comparisons
    let isVisible = false;
    if (
      typeof conditionValue === "number" &&
      typeof currentValue === "string"
    ) {
      isVisible = Number(currentValue) === conditionValue;
    } else if (
      typeof conditionValue === "string" &&
      typeof currentValue === "number"
    ) {
      isVisible = conditionValue === String(currentValue);
    } else {
      isVisible = currentValue === conditionValue;
    }

    return isVisible;
  };

  const clearNestedFields = (field: Field) => {
    if (field.nestedFields) {
      field.nestedFields.forEach((nested) => {
        setFieldValue(nested.name.toString(), "");
        handleFieldChange(nested.name, "");
        clearNestedFields(nested);
      });
    }
  };

  const getMaxValues = (locationTypeId: number) => {
    switch (locationTypeId) {
      case 1: // Urban
        return { length: 30, width: 30 };
      case 2: // Rural
        return { length: 35, width: 35 };
      case 3: // Hilly/Mountainous
        return { length: 20, width: 20 };
      default:
        return { length: 0, width: 0 };
    }
  };

  const resetAndPreserveFields = (
    preservedFields: Array<{ name: string; value: any }>,
  ) => {
    dispatch(resetSiteRoadWaysState());

    preservedFields.forEach(({ name, value }) => {
      dispatch(
        setSiteRoadwaysField({
          name,
          value,
        }),
      );
    });
  };

  useEffect(() => {
    const length = Number(values.plot_length_m);
    const width = Number(values.plot_width_m);
    const { length: maxLength, width: maxWidth } = getMaxValues(
      Number(values.location_type_id),
    );

    if (
      !isNaN(length) &&
      !isNaN(width) &&
      length >= maxLength &&
      width >= maxWidth
    ) {
      const area = length * width;
      setFieldValue("plot_area", Number(area));
      handleFieldChange("plot_area", Number(area));
    } else {
      setFieldValue("plot_area", null);
      handleFieldChange("plot_area", null);
    }
  }, [values.plot_length_m, values.plot_width_m, values.location_type_id]);

  useEffect(() => {
    if (isPrivate) return;

    if (isFirstLocationSet.current) {
      isFirstLocationSet.current = false;
      return;
    }

    resetAndPreserveFields([
      {
        name: "location_type_id",
        value: values?.location_type_id,
      },
    ]);
  }, [values?.location_type_id]);

  useEffect(() => {
    if (!isPrivate) return;

    if (isFirstServiceRoadSet.current) {
      isFirstServiceRoadSet.current = false;
      return;
    }

    resetAndPreserveFields([
      {
        name: "service_road_type_id",
        value: values?.service_road_type_id,
      },
      {
        name: "location_type_id",
        value: values?.location_type_id,
      },
      {
        name: "plot_length_m",
        value: values?.plot_length_m,
      },
      {
        name: "plot_width_m",
        value: values?.plot_width_m,
      },
      {
        name: "plot_area",
        value: values?.plot_area,
      },
    ]);
  }, [values?.service_road_type_id]);

  const plotFields = ["plot_length_m", "plot_width_m", "plot_area"];

  const loiFields = ["loi_name", "loi_dealer_name", "loi_number", "loi_date"];

  // Optional: Group titles and disclaimer text
  const sectionTitles: Record<string, string> = {
    plot: "Proposed Plot Details",
    loi: "Enter Letter of Intent(LOI) Details",
  };

  const loiDisclaimer =
    "It is hereby confirmed that the Letter of Intent (LOI) issued to the applicant remains valid and has not lapsed or expired as of the date of this verification.";

  const isPrivate = category_id === 2;

  const checkValidation = (field: Field, value: any): string | null => {
    if (!field.validation || !value) return null;

    // Automatically detect parent radio field with alwaysShowNested
    // Search through all sections to find if this field is nested under a radio with "No" selected
    const findParentRadio = (sections: Section[]): boolean => {
      for (const section of sections) {
        for (const parentField of section.fields || []) {
          if (
            parentField.type === "radio" &&
            parentField.alwaysShowNested &&
            parentField.nestedFields
          ) {
            // Check if current field is in this parent's nested fields
            const isNested = parentField.nestedFields.some(
              (nested: Field) => nested.name === field.name
            );
            if (isNested) {
              const parentValue = getIn(values, parentField.name.toString());
              // If parent radio is "No", skip validation
              return parentValue === "No";
            }
          }
        }
      }
      return false;
    };

    // Check if parent is "No" in dynamic sections
    if (findParentRadio(dynamicSections)) {
      return null;
    }

    const numValue = Number(value);
    if (isNaN(numValue)) return null;

    if (field.validation.min !== undefined && numValue < field.validation.min) {
      return field.validation.message || `At least ${field.validation.min}`;
    }

    if (field.validation.max !== undefined && numValue > field.validation.max) {
      return field.validation.message || `At most ${field.validation.max}`;
    }

    return null;
  };

  const getPlaceholder = (
    fieldName: string,
    locationTypeId: number,
  ): string => {
    if (isPrivate) {
      switch (fieldName) {
        case "plot_length_m":
          return "Frontage (in metres)";
        case "plot_width_m":
          return "Depth (in metres)";
        case "plot_area":
          return "Auto-calculated area";
        default:
          return "Enter value";
      }
    } else {
      const maxValues: MaxValues = getMaxValues(locationTypeId);
      switch (fieldName) {
        case "plot_length_m":
          return `Min Frontage: ${maxValues.length} m`;
        case "plot_width_m":
          return `Min Depth: ${maxValues.width} m`;
        case "plot_area":
          return "Auto-calculated area";
        case "loi_name":
          return "Enter Name";
        case "loi_dealer_name":
          return "Enter Dealer Name";
        case "loi_number":
          return "Enter LOI Number";
        case "loi_date":
          return "Select LOI Date";
        default:
          return "Enter value";
      }
    }
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    // Clear plot fields when location_type_id changes
    if (!values?.location_type_id) return;
    plotFields.forEach((field) => {
      setFieldValue(field, "");
      handleFieldChange(field, "");
    });
    // eslint-disable-next-line
  }, [values.location_type_id]);

  // Recursively render fields with nestedFields
  const renderField = (
    field: Field,
    isNested: boolean = false,
  ): React.ReactNode => {
    const fullName = field.name;
    const value = getIn(values, fullName.toString());
    const error = getIn(errors, fullName.toString());
    const touchedField = getIn(touched, fullName.toString());

    // Check if plot fields should be disabled
    const isPlotField = plotFields.includes(fullName.toString());
    const isLoiField = loiFields.includes(fullName.toString());
    const isDisabled = (isPlotField || isLoiField) && !values.location_type_id;

    // Common spacing and alignment props
    const gridProps = {
      item: true,
      xs: 12,
      key: fullName,
      sx: {
        mt: isNested ? 0 : 1,
        mb: isNested ? 1 : 1.5,
        mr: isPlotField || isLoiField ? 2 : 0,
        display: isPlotField || isLoiField ? "inline-block" : "flex",
        justifyContent:
          isPlotField || isLoiField ? "flex-start" : "space-between",
        alignItems: "center",
        opacity: isDisabled ? 0.6 : 1,
      },
    };

    // === Section Headers ===
    const shouldRenderPlotHeader = isPlotField && fullName === "plot_length_m"; // render once before first plot field
    const shouldRenderLoiHeader = isLoiField && fullName === "loi_name" && shouldShow(field); // render once before first LOI field (only if visible)
    const shouldRenderLoiFooter = isLoiField && fullName === "loi_date" && shouldShow(field); // render once after last LOI field (only if visible)
    const isRequired = field?.title?.includes("*");

    // Update the text field rendering section in UtilityFormRendererRetail
    if (field.type === "text" || field.type === "date") {
      const isRestricted = isFieldRestricted(field); // 🔥 NEW
      const isFieldDisabled = isDisabled || isRestricted; // 🔥 Combine with existing disabled
      // // Function to check if input is alphanumeric
      // const isAlphanumeric = (value: string): boolean => {
      //   // Allow letters (a-z, A-Z), numbers (0-9), and spaces
      //   return /^[a-zA-Z0-9\s]*$/.test(value);
      // };
      return (
        <>
          {shouldRenderPlotHeader && (
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 600,
                color: "#121212",
                mb: 0,
              }}
            >
              {sectionTitles.plot}
            </Typography>
          )}

          {shouldRenderLoiHeader && (
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 600,
                color: isFieldDisabled ? "text.disabled" : "#121212",
                mb: 0,
              }}
            >
              {sectionTitles.loi}
            </Typography>
          )}

          <Grid {...gridProps} className={"grid-item-text"}>
            <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
              <Typography
                variant="h6"
                className="font-standard"
                sx={{
                  color: isFieldDisabled ? "text.disabled" : "text.primary",
                }}
              >
                {field.title}
                <Box component="span" sx={{ color: "red" }}>
                  {isPlotField || isLoiField ? "*" : ""}
                </Box>
              </Typography>
            </Box>

            {field.type === "date" ? (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  open={openLoiDateModal}
                  disabled={isFieldDisabled}
                  onOpen={() => setOpenLoiDateModal(true)}
                  onClose={() => setOpenLoiDateModal(false)}
                  format={
                    fullName.toString() === "loi_date"
                      ? "DD/MM/YYYY"
                      : undefined
                  }
                  value={value ? dayjs(value) : null}
                  maxDate={dayjs()}
                  onChange={(newValue) => {
                    if (isFieldDisabled) return;

                    if (newValue) {
                      const isoDate = newValue.toISOString();
                      handleFieldChange(fullName, isoDate);
                    } else {
                      handleFieldChange(fullName, "");
                    }
                  }}
                  slotProps={{
                    textField: {
                      name: fullName.toString(),
                      sx: {
                        mb: 1,
                        width: isPlotField || isLoiField ? "250px" : "122px",
                        mx: isPlotField || isLoiField ? 0 : "end",
                        "& .MuiInputBase-input.Mui-disabled": {
                          WebkitTextFillColor: "rgba(0, 0, 0, 0.6)",
                        },
                        "& .MuiInputBase-input": {
                          padding: "10px 14px",
                        },
                      },
                      placeholder: isFieldDisabled
                        ? "Select location type first"
                        : getPlaceholder(
                          fullName.toString(),
                          Number(values.location_type_id),
                        ),
                      onBlur: handleBlur,
                      error: Boolean(touchedField && error),
                      helperText: touchedField && error,
                      inputProps: {
                        readOnly: true,
                      },
                      onClick: () => {
                        setOpenLoiDateModal(true);
                      },
                    },
                    popper: { sx: { zIndex: 20000 } },
                  }}
                />
              </LocalizationProvider>
            ) : (
              <Box sx={{ width: isPlotField || isLoiField ? "250px" : "122px", flexShrink: 0 }}>
                <StyledTextField
                  name={fullName.toString()}
                  value={value || ""}
                  disabled={isFieldDisabled}
                  sx={{
                    width: "100%",
                    "& input[type=number]": {
                      "-moz-appearance": "textfield",
                      "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
                        "-webkit-appearance": "none",
                        margin: 0,
                      },
                    },
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "rgba(0, 0, 0, 0.6)",
                    },
                  }}
                  inputProps={
                    field.dataType === "number"
                      ? { inputMode: "decimal" }
                      : field.dataType === "string"
                        ? { inputMode: "text" }
                        : undefined
                  }
                  onChange={(e) => {
                    if (isFieldDisabled) return;

                    let newValue = e.target.value;

                    // Security: Sanitize input to prevent XSS and injection attacks
                    if (field.dataType !== "number") {
                      const sanitized = sanitizeInput(
                        newValue,
                        fullName.toString(),
                      );
                      if (sanitized === null && newValue !== "") {
                        return; // Reject dangerous or invalid input
                      }
                    }

                    // Custom validation for loi_dealer_name - only letters and spaces
                    if (
                      fullName.toString() === "loi_dealer_name" ||
                      fullName.toString() === "loi_name"
                    ) {
                      // Allow only letters (a-z, A-Z), spaces, dots, and common name characters
                      const nameRegex = /^[a-zA-Z\s.'-]*$/;
                      if (!nameRegex.test(newValue)) {
                        return; // Prevent input if it contains numbers or special characters
                      }
                    }

                    // Custom validation for loi_number - alphanumeric only
                    if (fullName.toString() === "loi_number") {
                      // Allow only letters and numbers (no special characters except maybe /)
                      const alphanumericRegex = /^[a-zA-Z0-9/\-]*$/;
                      if (!alphanumericRegex.test(newValue)) {
                        return; // Prevent input if it contains invalid characters
                      }
                    }

                    if (field.dataType === "string") {
                      // Define what characters to allow based on field context
                      const allowedPatterns = {
                        alphanumeric: /^[a-zA-Z0-9\s]*$/, // Letters, numbers, spaces
                        alphanumericWithDecimal: /^[a-zA-Z0-9\s.]*$/, // + decimal point
                        numericWithDecimal: /^-?\d*\.?\d*$/, // Only numbers with optional decimal
                        alphanumericWithCommonSymbols: /^[a-zA-Z0-9\s.,\-_@]*$/, // + common symbols
                      };

                      // Use the appropriate pattern or a default one
                      const pattern = allowedPatterns.alphanumericWithDecimal;

                      if (!pattern.test(newValue)) {
                        return; // Block invalid input
                      }
                    }

                    if (field.dataType === "number" && newValue !== "") {
                      newValue = newValue.replace(/-/g, "");
                      if (newValue !== "" && isNaN(Number(newValue))) {
                        return;
                      }
                    }

                    if (
                      fullName.toString() === "plot_area" &&
                      newValue !== ""
                    ) {
                      const numericValue = Number(newValue);
                      handleFieldChange(fullName, numericValue);
                    } else {
                      handleFieldChange(fullName, newValue);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (isFieldDisabled) return;

                    // // 🔥 NEW: Block special characters for string fields
                    // if (field.dataType === "string") {
                    //   const key = e.key;
                    //   // Allow: Backspace, Delete, Tab, Enter, Escape, Arrow keys, Home, End, etc.
                    //   const allowedControlKeys = [
                    //     'Backspace', 'Delete', 'Tab', 'Enter', 'Escape',
                    //     'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
                    //     'Home', 'End', 'PageUp', 'PageDown'
                    //   ];

                    //   if (allowedControlKeys.includes(key)) {
                    //     return; // Allow these keys
                    //   }

                    //   // Block special characters
                    //   const specialChars = /[^a-zA-Z0-9\s]/;
                    //   if (specialChars.test(key) && key.length === 1) {
                    //     e.preventDefault();
                    //     return;
                    //   }
                    // }

                    if (
                      field.dataType === "number" &&
                      (e.key === "-" || e.key === "Subtract")
                    ) {
                      e.preventDefault();
                    }
                  }}
                  placeholder={
                    isFieldDisabled
                      ? "Select location type first"
                      : getPlaceholder(
                        fullName.toString(),
                        Number(values.location_type_id),
                      )
                  }
                  onBlur={handleBlur}
                  error={
                    Boolean(touchedField && error) ||
                    Boolean(checkValidation(field, value))
                  }
                  helperText={
                    touchedField && error
                      ? error
                      : checkValidation(field, value)
                  }
                  type="text"
                />
              </Box>
            )}
          </Grid>

          {shouldRenderLoiFooter && (
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 500,
                color: "#996601",
                display: "flex",
                alignItems: "center",
                mb: 2,
                mt: -2,
              }}
            >
              <InfoOutlined sx={{ mr: 0.5 }} /> <em>{loiDisclaimer}</em>
            </Typography>
          )}
        </>
      );
    }

    if (field.type === "radio") {
      const isRestricted = isFieldRestricted(field);
      return (
        <>
          <Grid {...gridProps} className={"grid-item-radio"}>
            <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
              <Typography variant="h6" className="item-radio-title font-standard" sx={{ width: "95%" }}>
                {field.title.replace(/\*/g, "")}
                {isRequired && <span style={{ color: "red" }}> *</span>}
              </Typography>
            </Box>
            <Box sx={{ flexShrink: 0 }}>
              <FormControl error={Boolean(touchedField && error)} disabled={isRestricted}>
                <RadioGroup
                  row
                  name={fullName.toString()}
                  value={value ?? ""}
                  sx={{ flexWrap: "nowrap" }}
                  className="item-radio-group"
                  onChange={(e) => {
                    if (isRestricted) return;
                    const isObjectOptions =
                      field.options && typeof field.options[0] === "object";
                    const objectOptions = isObjectOptions
                      ? ((field.options ?? []) as unknown as Array<{
                        id: string | number;
                        name: string;
                      }>)
                      : [];
                    const selectedOption = objectOptions.find(
                      (option) => String(option.id) === String(e.target.value),
                    );
                    const newValue = isObjectOptions
                      ? selectedOption?.id ?? e.target.value
                      : e.target.value;

                    handleFieldChange(fullName, newValue);

                    if (field.nestedFields && newValue !== "Yes") {
                      clearNestedFields(field);
                    }
                  }}
                >
                  {field.options?.map((option: any) =>
                    typeof option === "object" && option !== null ? (
                      <FormControlLabel
                        key={option.id}
                        value={option.id}
                        control={<Radio className="radio-button " />}
                        label={option.name}
                        disabled={isRestricted}
                      />
                    ) : (
                      <FormControlLabel
                        key={option}
                        value={option}
                        control={<Radio className="radio-button " />}
                        label={option}
                        disabled={isRestricted}
                      />
                    ),
                  )}
                </RadioGroup>
              </FormControl>
              {touchedField && typeof error === "string" && (
                <Typography sx={{ color: "red", fontSize: "0.75rem", mt: 0.5 }}>
                  {error}
                </Typography>
              )}
            </Box>
          </Grid>
          {field.nestedFields &&
            (field.alwaysShowNested ||
              value === "Yes" ||
              (typeof value === "number" && value !== 0) ||
              (value !== "" &&
                value !== undefined &&
                field.type !== "radio")) && (
              <Grid
                item
                xs={12}
                sx={{ pl: 3, borderLeft: "1px solid #e0e0e0", mt: 1 }}
              >
                <Grid container spacing={0}>
                  {field.nestedFields.map((nested) =>
                    renderField(nested, true),
                  )}
                </Grid>
              </Grid>
            )}
        </>
      );
    }
    if (field.type === "select") {
      const isRestricted = isFieldRestricted(field);
      return (
        <Grid {...gridProps} className={"grid-item-select"}>
          <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
            <Typography variant="h6" className="font-standard">{field.title}</Typography>
          </Box>
          <Box sx={{ width: "200px", flexShrink: 0 }}>
            <FormControl fullWidth error={Boolean(touchedField && error)} disabled={isRestricted}>
              <Select
                name={fullName.toString()}
                value={value || ""}
                onBlur={handleBlur}
                disabled={isRestricted}
                size="small"
                sx={{ borderRadius: 2 }}
                onChange={(e) => {
                  if (isRestricted) return;
                  const selectedValue = e.target.value;
                  handleFieldChange(fullName, selectedValue);
                }}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Select
                </MenuItem>
                {field.options?.map((option: any) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
              {touchedField && typeof error === "string" && (
                <Typography sx={{ color: "red", fontSize: "0.75rem", mt: 0.5 }}>
                  {error}
                </Typography>
              )}
            </FormControl>
          </Box>
          {field.nestedFields && value !== "" && value !== undefined && (
            <Grid
              item
              xs={12}
              sx={{ pl: 3, borderLeft: "3px solid #e0e0e0", mt: 1 }}
            >
              <Grid container spacing={0}>
                {field.nestedFields.map((nested) => renderField(nested, true))}
              </Grid>
            </Grid>
          )}
        </Grid>
      );
    }
    if (field.name === "location_type_id") {
      const isRestricted = isFieldRestricted(field);
      return (
        <Grid
          {...gridProps}
          className={"grid-item-radio-" + fullName.toString()}
        >
          <Typography variant="h6" className="font-standard">{field.title}</Typography>
          <FormControl error={Boolean(touchedField && error)} sx={{ mt: 1 }} disabled={isRestricted}>
            <RadioGroup
              row
              name={fullName.toString()}
              value={value ?? ""}
              onChange={(e) => {
                if (isRestricted) return;
                const selectedValue = e.target.value;
                handleFieldChange(fullName.toString(), selectedValue);
              }}
            >
              {field.options?.map((option) => {
                const optionValue =
                  typeof option === "object" && option !== null
                    ? option.id
                    : option;
                const optionLabel =
                  typeof option === "object" && option !== null
                    ? option.name
                    : option;

                return (
                  <FormControlLabel
                    key={String(optionValue)}
                    value={optionValue}
                    control={<Radio className="radio-button " />}
                    label={optionLabel}
                    disabled={isRestricted}
                  />
                );
              })}
            </RadioGroup>
            {touchedField && typeof error === "string" && (
              <Typography sx={{ color: "red", fontSize: "0.75rem", mt: 0.5 }}>
                {error}
              </Typography>
            )}
          </FormControl>
        </Grid>
      );
    }
    return null;
  };

  // useEffect handling changes for is_constructed_without_prior_permission
  useEffect(() => {
    const constructed = values.is_constructed_without_prior_permission;
    const operational = values.is_facility_operational;

    if (constructed === "Yes" && (operational === "No" || !operational)) {
      setShowFuelStationDialog(true);
      setShowEnergizedFuelDialog(false);
    } else if (constructed === "Yes" && operational === "Yes") {
      // Both "Yes" but constructed key changed last, show FuelStationDialog only
      setShowFuelStationDialog(false);
      setShowEnergizedFuelDialog(false);
    } else {
      setShowFuelStationDialog(false);
    }
  }, [values.is_constructed_without_prior_permission]);

  // useEffect handling changes for is_facility_operational
  useEffect(() => {
    const constructed = values.is_constructed_without_prior_permission;
    const operational = values.is_facility_operational;

    if (operational === "Yes" && (constructed === "No" || !constructed)) {
      setShowEnergizedFuelDialog(true);
      setShowFuelStationDialog(false);
    } else if (operational === "Yes" && constructed === "Yes") {
      // Both "Yes" but operational key changed last, show EnergizedFuelDialog only
      setShowEnergizedFuelDialog(false);
      setShowFuelStationDialog(false);
    } else {
      setShowEnergizedFuelDialog(false);
    }
  }, [values.is_facility_operational]);

  useEffect(() => {
    const selectedCarriagewayType = values.fuel_station_carriageway_type;

    if (selectedCarriagewayType === "Yes") {
      setShowDecelerationlaneDialog(
        values["78"] === "No" || values["77"] === "No" || values["299"] === "No",
      );
      return;
    }

    if (selectedCarriagewayType === "No") {
      setShowDecelerationlaneDialog(
        values["399"] === "No" || values["432"] === "No" || values["433"] === "No",
      );
      return;
    }

    setShowDecelerationlaneDialog(false);
  }, [
    values.fuel_station_carriageway_type,
    values["78"],
    values["299"],
    values["77"],
    values["399"],
    values["432"],
    values["433"],
  ]);

  useEffect(() => {
    if (values["1"] === "No" || values["24"] === "No") {
      setIntersectionRoadMedianDialog(true);
    } else {
      setIntersectionRoadMedianDialog(false);
    }
  }, [values["1"], values["24"]]);

  useEffect(() => {
    if (values["96"] === "No") {
      setShowNearestIntersectionDialog(true);
    } else {
      setShowNearestIntersectionDialog(false);
    }
  }, [values["96"]]);

  useEffect(() => {
    if (
      values.fuel_station_carriageway_type === "No" &&
      values["399"] === "No"
    ) {
      setShowProximityServiceRoadDialog(true);
    } else {
      setShowProximityServiceRoadDialog(false);
    }
  }, [values.fuel_station_carriageway_type, values["399"]]);

  useEffect(() => {
    if (values["70"] === "No" || values["71"] === "No") {
      setShowRoadNotCompliedDialog(true);
    } else {
      setShowRoadNotCompliedDialog(false);
    }
  }, [values["70"], values["71"]]);

  interface HandleStepClick {
    (index: number): void;
  }

  const handleStepClick: HandleStepClick = (index) => {
    setActiveStep(index);
    const sectionElement: HTMLElement | null = document.getElementById(
      `section-${index}`,
    );
    if (sectionElement) {
      sectionElement.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  type FieldName = string; // or a union of your form field keys for stricter typing
  type SetShowDialog = React.Dispatch<React.SetStateAction<boolean>>;


  const closeDialog =
    (mainField: FieldName, consentField: FieldName) =>
      (setShowDialog: SetShowDialog) =>
        (): void => {
          setFieldValue(mainField, "");
          handleFieldChange(mainField, "");
          setFieldValue(consentField, ""); // Reset the consent field when closing the dialog
          setShowDialog(false);
        };

  const confirmDialog =
    (consentField: FieldName) =>
      (setShowDialog: SetShowDialog) =>
        (): void => {
          setFieldValue(consentField, true);
          setShowDialog(false);
        };

  // Render all sections
  return (
    <Box pt={1}>
      <FuelStationDialog
        open={showFuelStationDialog}
        penaltyFeeData={penaltyFeeData}
        onClose={closeDialog(
          "is_constructed_without_prior_permission",
          "is_constructed_without_prior_permission_consent"
        )(setShowFuelStationDialog)}
        onConfirm={confirmDialog(
          "is_constructed_without_prior_permission_consent"
        )(setShowFuelStationDialog)}
      />
      <EnergizedFuelDialog
        open={showEnergizedFuelDialog}
        penaltyFeeData={penaltyFeeData}
        onClose={closeDialog(
          "is_facility_operational",
          "is_facility_operational_consent"
        )(setShowEnergizedFuelDialog)}
        onConfirm={confirmDialog(
          "is_facility_operational_consent"
        )(setShowEnergizedFuelDialog)}
      />

      <RoadNotCompliedDialog
        open={showRoadNotCompliedDialog}
        onClose={() => {
          setFieldValue("70", "");
          setFieldValue("71", "");
          handleFieldChange("70", "");
          handleFieldChange("71", "");
          setShowRoadNotCompliedDialog(false);
        }}
        onConfirm={() => {
          setShowRoadNotCompliedDialog(false);
        }}
      />
      <DecelerationlaneDialog
        open={showDecelerationlaneDialog}
        onClose={() => {
          setFieldValue("78", "");
          setFieldValue("77", "");
          setFieldValue("299", "");
          setFieldValue("399", "");
          setFieldValue("432", "");
          setFieldValue("433", "");
          handleFieldChange("78", "");
          handleFieldChange("77", "");
          handleFieldChange("299", "");
          handleFieldChange("399", "");
          handleFieldChange("432", "");
          handleFieldChange("433", "");
          setShowDecelerationlaneDialog(false);
        }}
        onConfirm={() => {
          setShowDecelerationlaneDialog(false);
        }}
      />

      <NearestIntersectionDialog
        open={showNearestIntersectionDialog}
        onClose={() => {
          setFieldValue("96", "");
          handleFieldChange("96", "");
          setShowNearestIntersectionDialog(false);
        }}
        onConfirm={() => {
          setShowNearestIntersectionDialog(false);
        }}
      />
      <ProximityServiceRoadDialog
        open={showProximityServiceRoadDialog}
        onClose={() => {
          setFieldValue("399", "");
          handleFieldChange("399", "");
          setShowProximityServiceRoadDialog(false);
        }}
        onConfirm={() => {
          setShowProximityServiceRoadDialog(false);
        }}
      />
      <IntersectionRoadMedianDialog
        open={intersectionRoadMedianDialog}
        onClose={() => {
          setFieldValue("1", "");
          setFieldValue("24", "");
          handleFieldChange("1", "");
          handleFieldChange("24", "");
          setIntersectionRoadMedianDialog(false);
        }}
        onConfirm={() => {
          setIntersectionRoadMedianDialog(false);
        }}
      />
      <DividedCarriagewayDialog
        open={showDividedCarriagewayDialog}
        onClose={() => {
          setShowDividedCarriagewayDialog(false);
        }}
        onConfirm={() => {
          setShowDividedCarriagewayDialog(false);
        }}
      />
      <Box
        sx={{
          position: "sticky",
          top: 0,
          backgroundColor: "#fff",
          zIndex: 100,
          py: 0.8,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            overflowX: "auto",
            // textWrap: "nowrap",
            // whiteSpace: "nowrap",
            gap: 0,
            width: "100%",
          }}
        >
          {dynamicSections.map((section, i) => (
            <Box
              key={i}
              onClick={() => handleStepClick(i)}
              sx={{
                position: "relative",
                flex: 1,
                marginInline: "8px",
                height: "50px",
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: activeStep === i ? "#3a1c73" : "#c4c4c4",
                color: "#fff",
                fontSize: "10px",
                fontWeight: 600,
                cursor: "pointer",
                clipPath:
                  i === 0
                    ? "polygon(0% 0%, calc(100% - 20px) 0%, 100% 50%, calc(100% - 20px) 100%, 0% 100%)"
                    : "polygon(0% 0%, calc(100% - 20px) 0%, 100% 50%, calc(100% - 20px) 100%, 0% 100%, 20px 50%, 0% 0%)",
                opacity: activeStep === i ? 1 : 0.7,
                marginLeft: i === 0 ? "0" : "-20px",
                paddingLeft: i === 0 ? "20px" : "30px",
                paddingRight:
                  i === dynamicSections.length - 1 ? "20px" : "30px",
                zIndex: activeStep === i ? 2 : 1,
                transition: "all 0.3s ease",
                "&:hover": {
                  opacity: 0.9,
                },
              }}
            >
              {section.title}
            </Box>
          ))}
        </Box>
      </Box>

      {dynamicSections.map((section, i) => (
        <Box
          key={`section-${i}`}
          mb={2}
          id={"section-" + i}
          className="section-box"
          sx={{ scrollMarginTop: "10vh" }}
        >
          <Typography
            variant="h6"
            sx={{
              fontSize: "1.5rem",
              fontWeight: "700",
              color: "#3a1c73",
              borderBottom: "2px solid #3a1c73",
              pb: 1,
            }}
          >
            {i + 1}. {section.title}
          </Typography>
          <Box
            className="section-fields"
            sx={{ width: "100%", overflow: "hidden" }}
          >
            {section.fields
              .filter((field: Field) => {
                const isVisible = shouldShow(field);
                return isVisible;
              })
              .map((field: Field) => renderField(field))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export const validationSchema = Yup.object({
  location_type_id: Yup.string().required("Required"),
  is_coco_fuel_station: Yup.string().required("Required"),
  loi_name: Yup.string().when("is_coco_fuel_station", {
    is: "No",
    then: (schema) => schema.required("Required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  loi_dealer_name: Yup.string().when("is_coco_fuel_station", {
    is: "No",
    then: (schema) => schema.required("Required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  loi_number: Yup.string().when("is_coco_fuel_station", {
    is: "No",
    then: (schema) => schema.required("Required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  loi_date: Yup.string().when("is_coco_fuel_station", {
    is: "No",
    then: (schema) => schema.required("Required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  // othersPropertyType: Yup.string().when('propertyType', {
  //     is: 'Others (Please Specify – with remarks)',
  //     then: schema => schema.required('Please specify the property type'),
  //     otherwise: schema => schema.notRequired()
  // }),
  // plot_length_m: Yup.number()
  //     .typeError("Must be a number")
  //     .max(30, "Maximum allowed is 30")
  //     .required("Required"),
  // plot_width_m: Yup.number()
  //     .typeError("Must be a number")
  //     .max(35, "Maximum allowed is 35")
  //     .required("Required"),
  // plot_area: Yup.number()
  //     .typeError("Must be a number")
  //     .required("Required"),
});

// for Retail whole data
export const sectionsRetail: Section[] = [
  {
    title: "Property & Site Location",
    fields: [
      {
        title: "Select the stretch type of proposed site / location",
        name: "location_type_id",
        type: "select",
        options: SelectAmenityCategoryRetail,
      },
      {
        title: "Select the type of fuel station allocated to the applicant",
        name: "is_coco_fuel_station",
        type: "radio",
        options: [
          { id: "Yes", name: "COCO (Company Owned Company Operated)" },
          { id: "No", name: "Other than COCO" },
        ],
      },
      {
        title: "Name of LOI Issuing Authority",
        name: "loi_name",
        type: "text",
        showIf: { field: "is_coco_fuel_station", value: "No" },
      },
      {
        title: "Name of the Dealer",
        name: "loi_dealer_name",
        type: "text",
        showIf: { field: "is_coco_fuel_station", value: "No" },
      },
      {
        title: "LOI Number",
        name: "loi_number",
        type: "text",
        showIf: { field: "is_coco_fuel_station", value: "No" },
      },
      {
        title: "LOI Issue Date",
        name: "loi_date",
        type: "date",
        showIf: { field: "is_coco_fuel_station", value: "No" },
      },
      {
        title: "Average Frontage (in metres)",
        name: "plot_length_m",
        type: "text",
        dataType: "number",
      },
      {
        title: "Average Depth (in metres)",
        name: "plot_width_m",
        type: "text",
        dataType: "number",
      },
      {
        title: "Area (in square metres)",
        name: "plot_area",
        type: "text",
        dataType: "number",
      },
      ...notPreviouslyApproved,
    ],
  },
];

export const sectionRetailSeekClarification = [
  {
    title: "Property & Site Location",
    fields: [
      {
        title: "Select the stretch type of proposed site / location",
        name: "location_type_id",
        type: "select",
        options: SelectAmenityCategoryRetail,
      },
      {
        title: "Select the type of fuel station allocated to the applicant",
        name: "is_coco_fuel_station",
        type: "radio",
        options: [
          { id: "Yes", name: "COCO (Company Owned Company Operated)" },
          { id: "No", name: "Other than COCO" },
        ],
      },
      {
        title: "Name of LOI Issuing Authority",
        name: "loi_name",
        type: "text",
        showIf: { field: "is_coco_fuel_station", value: "No" },
      },
      {
        title: "Name of the Dealer",
        name: "loi_dealer_name",
        type: "text",
        showIf: { field: "is_coco_fuel_station", value: "No" },
      },
      {
        title: "LOI Number",
        name: "loi_number",
        type: "text",
        showIf: { field: "is_coco_fuel_station", value: "No" },
      },
      {
        title: "LOI Issue Date",
        name: "loi_date",
        type: "date",
        showIf: { field: "is_coco_fuel_station", value: "No" },
      },
      {
        title: "Average Frontage (in metres)",
        name: "plot_length_m",
        type: "text",
        dataType: "number",
      },
      {
        title: "Average Depth (in metres)",
        name: "plot_width_m",
        type: "text",
        dataType: "number",
      },
      {
        title: "Area (in square metres)",
        name: "plot_area",
        type: "text",
        dataType: "number",
      },
      ...notPreviouslyApprovedSeekClarification,
    ],
  },
];

// for Private whole data
export const sectionPrivate: Section[] = [
  {
    title: "Property & Site Location",
    fields: [
      {
        title: "Select the stretch type of proposed site / location",
        name: "location_type_id",
        type: "select",
        options: SelectAmenityCategoryPrivate,
      },
      {
        title: "Average Frontage (in metres)",
        name: "plot_length_m",
        type: "text",
        dataType: "number",
      },
      {
        title: "Average Depth (in metres)",
        name: "plot_width_m",
        type: "text",
        dataType: "number",
      },
      {
        title: "Area (in square metres)",
        name: "plot_area",
        type: "text",
        dataType: "number",
      },
      {
        title: "Select Service Road Type",
        name: "service_road_type_id",
        type: "select",
        options: SelectServiceRoadTypePrivate,
      },
      ...ifExistingSlipRoad,
    ],
  },
];